<script setup lang="ts">
import { Tooltip } from "floating-vue";
import { computed } from "vue";
import { AbilityLevel } from "../core/ablvl";

type AbilityLevelKey = "pAtk" | "pDef" | "mAtk" | "mDef" | "speed" | "hit";

type AbilityConfig = {
    key: AbilityLevelKey;
    name: string;
    icon: string;
};

const props = defineProps<{
    abLvl: AbilityLevel;
    pos: "left" | "right";
}>();

const abilities: AbilityConfig[] = [
    { key: "pAtk", name: "攻击", icon: "/assets/sword.svg" },
    { key: "pDef", name: "防御", icon: "/assets/shield.svg" },
    { key: "mAtk", name: "特攻", icon: "/assets/sparkle.svg" },
    { key: "mDef", name: "特防", icon: "/assets/shield-plus.svg" },
    { key: "speed", name: "速度", icon: "/assets/rocket.svg" },
    { key: "hit", name: "命中", icon: "/assets/crosshair.svg" },
];

const visibleAbilities = computed(() =>
    abilities
        .map((ability) => ({
            ...ability,
            level: props.abLvl[ability.key],
        }))
        .filter((ability) => ability.level !== 0)
);

const placement = computed(() =>
    props.pos === "right" ? "bottom-end" : "bottom-start"
);

function formatLevel(level: number): string {
    return level > 0 ? `+${level}` : `${level}`;
}
</script>

<template>
    <div
        v-if="visibleAbilities.length > 0"
        :class="['ability-level-group', pos]"
    >
        <Tooltip
            v-for="ability in visibleAbilities"
            :key="ability.key"
            :placement="placement"
        >
            <button
                type="button"
                :class="[
                    'ability-level-btn',
                    ability.level > 0 ? 'is-up' : 'is-down',
                ]"
                :aria-label="`${ability.name}等级 ${formatLevel(ability.level)}`"
            >
                <img :src="ability.icon" alt="" />
                <span>{{ formatLevel(ability.level) }}</span>
            </button>
            <template #popper>
                <span class="ability-level-tip">
                    {{ ability.name }}等级 {{ formatLevel(ability.level) }}
                </span>
            </template>
        </Tooltip>
    </div>
</template>

<style scoped>
.ability-level-group {
    /* 让各项能力与父级中的护盾、护罩、抗性共同换行和镜像排列。 */
    display: contents;
}

.ability-level-btn {
    box-sizing: border-box;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 calc(var(--ui-unit) * 32);
    width: calc(var(--ui-unit) * 32);
    height: calc(var(--ui-unit) * 32);
    padding: 0;
    border: calc(var(--ui-unit) * 1) solid;
    border-radius: calc(var(--ui-unit) * 6);
    background: rgba(0, 0, 0, 0.38);
    transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
}

.ability-level-btn img {
    display: block;
    width: calc(var(--ui-unit) * 22);
    height: calc(var(--ui-unit) * 22);
    object-fit: contain;
    filter: brightness(0) invert(1);
    pointer-events: none;
}

.ability-level-btn span {
    position: absolute;
    right: calc(var(--ui-unit) * 1);
    bottom: calc(var(--ui-unit) * 1);
    min-width: calc(var(--ui-unit) * 12);
    padding: 0 calc(var(--ui-unit) * 2);
    border: calc(var(--ui-unit) * 1) solid currentColor;
    border-radius: calc(var(--ui-unit) * 4) 0 calc(var(--ui-unit) * 4) 0;
    background: rgba(10, 12, 22, 0.9);
    font-variant-numeric: tabular-nums;
    font-size: calc(var(--ui-unit) * 9);
    font-weight: 800;
    line-height: calc(var(--ui-unit) * 12);
    pointer-events: none;
}

.ability-level-btn.is-up {
    border-color: rgba(249, 115, 22, 0.88);
    color: #fb923c;
}

.ability-level-btn.is-up:hover,
.ability-level-btn.is-up:focus-visible {
    border-color: #fb923c;
    background: rgba(249, 115, 22, 0.12);
    box-shadow: 0 0 calc(var(--ui-unit) * 9) rgba(249, 115, 22, 0.38);
}

.ability-level-btn.is-down {
    border-color: rgba(59, 130, 246, 0.88);
    color: #60a5fa;
}

.ability-level-btn.is-down:hover,
.ability-level-btn.is-down:focus-visible {
    border-color: #60a5fa;
    background: rgba(59, 130, 246, 0.12);
    box-shadow: 0 0 calc(var(--ui-unit) * 9) rgba(59, 130, 246, 0.38);
}

.ability-level-btn:focus-visible {
    outline: calc(var(--ui-unit) * 2) solid rgba(255, 255, 255, 0.85);
    outline-offset: calc(var(--ui-unit) * 2);
}

.ability-level-tip {
    white-space: nowrap;
    font-weight: 700;
}
</style>
